import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IntelligenceService, SmartSampleResult } from 'src/app/intelligence/intelligence.service';

export interface SmartSampleDialogData {
  inputCacheKey:         string;
  sourceColumn:          string;
  availableSourceFields: string[];
  currentSampleStart:    number;
  currentSampleEnd:      number;
  smartSampleMode:       number;
}

@Component({
  selector: 'app-smart-sample-dialog',
  templateUrl: './smart-sample-dialog.component.html',
  styleUrls: ['./smart-sample-dialog.component.css'],
  standalone: false,
})
export class SmartSampleDialogComponent implements OnInit {
  loading       = true;
  result:SmartSampleResult | null = null;
  error         = '';
  alreadyApplied = false;
  selectedSourceField = '';

  constructor(
    private dialogRef: MatDialogRef<SmartSampleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SmartSampleDialogData,
    private intelligenceService: IntelligenceService,
  ) {}

  ngOnInit(): void {
    this.selectedSourceField = this.data.sourceColumn ?? '';
    this.fetchSmartSample(this.selectedSourceField);
  }

  /** Re-run when the Analyst picks a different Source Field from the dropdown. */
  onSourceFieldChange(field: string): void {
    this.selectedSourceField = field;
    this.loading = true;
    this.error   = '';
    this.result  = null;
    this.fetchSmartSample(field);
  }

  private fetchSmartSample(sourceColumn: string): void {
    this.intelligenceService
      .getSmartSample(this.data.inputCacheKey, sourceColumn, this.data.smartSampleMode ?? 0)
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res.error) { this.error = res.error; return; }
          this.result = res;
          // viewModel.parameters.sampleStart/sampleEnd (what this dialog writes)
          // are 1-based inclusive (see extractorViewModel.ts's config+1/-1
          // load/save conversion). start_row is 0-based inclusive, so +1 makes
          // it 1-based. end_row is 0-based EXCLUSIVE, which is already
          // numerically equal to the 1-based inclusive last row — no further
          // adjustment (matches suggest_sample_range.py's ui_end = end_row).
          const recStart = res.start_row + 1;
          const recEnd   = res.end_row;
          this.alreadyApplied =
            res.cached &&
            recStart === this.data.currentSampleStart &&
            recEnd   === this.data.currentSampleEnd;
        },
        error: (err) => {
          this.loading = false;
          this.error   = err?.error?.error ?? err?.message ?? 'An unexpected error occurred';
        },
      });
  }

  apply(): void {
    if (!this.result) return;
    this.dialogRef.close({
      sampleStart: this.result.start_row + 1,
      sampleEnd:   this.result.end_row,
      sortBy:      this.result.sort_field ?? undefined,
    });
  }

  /** Extra digit of precision below 1% — otherwise "0.0%" reads as empty rather than tiny. */
  formatPct(pct: number): string {
    return (pct < 1 ? pct.toFixed(2) : pct.toFixed(1)) + '%';
  }

  cancel(): void {
    if (this.alreadyApplied && this.result) {
      this.apply();
    } else {
      this.dialogRef.close(null);
    }
  }
}
